/**
 * LeadPaintElectionField — the buyer's explicit lead-based paint election on
 * a Wisconsin residential or condo offer for a home built before 1978, or
 * whose year built the listing doesn't give (user decision; legal copy file
 * §10, `WI.offer.lead_paint.*`).
 *
 * The buyer must choose one:
 *   - inspect: keep the federal inspection opportunity, within N days
 *     (`lead_paint_election: 'inspect'`, `lead_paint: true`, `lead_paint_days`;
 *     prefilled with 10);
 *   - waive: give it up in writing (`lead_paint_election: 'waive'`).
 *
 * Rendered by the offer wizard's Terms step and both counter forms, only when
 * `leadPaintElectionMode` returns a mode. The value lives in the form's
 * `contingencies` object; see `applyLeadPaintElection` and
 * `serializeContingencies` in contingencyCatalog.js.
 *
 * The election is the buyer's right: the seller's counter forms render
 * `LeadPaintElectionCarried` instead, a read-only line with the buyer's
 * stored election, which the counter carries forward unchanged.
 *
 * Props:
 *   - mode: 'pre_1978' | 'unknown_year' (from `leadPaintElectionMode`)
 *   - contingencies: form state
 *   - onChange(nextContingencies)
 *   - disabled
 *   - serverErrors: `{lead_paint_election, lead_paint_days}` messages from a 422
 */
import React, { useId } from 'react';
import {
  CONTINGENCY_DEADLINE_REQUIRED_MESSAGE,
  DEADLINE_INVALID_MESSAGE,
  LEAD_PAINT_COPY as COPY,
  LEAD_PAINT_ELECTION_KEY,
  LEAD_PAINT_INSPECT,
  LEAD_PAINT_KEY,
  LEAD_PAINT_MODE_UNKNOWN_YEAR,
  LEAD_PAINT_WAIVE,
  MAX_DEADLINE_DAYS,
  MIN_DEADLINE_DAYS,
  applyLeadPaintElection,
  deadlineKey,
  getLeadPaintElection,
  getLeadPaintElectionSummary,
  parseDeadlineDays,
} from './contingencyCatalog';
import { copyText } from '../SellerDashboard/paperworkCopy';
import styles from './LeadPaintElectionField.module.css';

const DAYS_KEY = deadlineKey(LEAD_PAINT_KEY);

export default function LeadPaintElectionField({
  mode,
  contingencies,
  onChange,
  disabled = false,
  serverErrors = null,
}) {
  const id = useId();
  const questionId = `${id}-question`;
  const helpId = `${id}-help`;
  const daysId = `${id}-days`;
  const daysHelpId = `${id}-days-help`;
  const daysErrorId = `${id}-days-error`;
  const missingId = `${id}-missing`;
  const waiveNoteId = `${id}-waive-note`;

  if (!mode) return null;

  const election = getLeadPaintElection(contingencies);
  const daysText =
    contingencies?.[DAYS_KEY] === undefined || contingencies?.[DAYS_KEY] === null
      ? ''
      : String(contingencies[DAYS_KEY]);
  const days = parseDeadlineDays(daysText);
  let daysError = null;
  if (election === LEAD_PAINT_INSPECT) {
    if (days === undefined) daysError = DEADLINE_INVALID_MESSAGE;
    else if (days === null) daysError = CONTINGENCY_DEADLINE_REQUIRED_MESSAGE;
  }
  const electionServerError = serverErrors?.[LEAD_PAINT_ELECTION_KEY] || null;
  const daysServerError = serverErrors?.[DAYS_KEY] || null;

  const choose = (value) => onChange(applyLeadPaintElection(contingencies, value));
  const setDays = (text) => onChange({ ...(contingencies || {}), [DAYS_KEY]: text });

  return (
    <fieldset
      className={styles.election}
      data-testid="lead-paint-election"
      data-election={election || undefined}
      aria-describedby={helpId}
    >
      <legend className={styles.title}>
        {COPY.title}
        <span className={styles.required} aria-hidden="true"> *</span>
      </legend>
      <p id={helpId} className={styles.help}>
        {mode === LEAD_PAINT_MODE_UNKNOWN_YEAR ? COPY.helpUnknownYear : COPY.help}
      </p>
      <p id={questionId} className={styles.question}>
        {COPY.question}
      </p>

      <div
        role="radiogroup"
        aria-labelledby={questionId}
        aria-required="true"
        aria-describedby={!election ? missingId : undefined}
        className={styles.options}
      >
        <label className={`${styles.option} ${election === LEAD_PAINT_INSPECT ? styles.optionOn : ''}`}>
          <input
            type="radio"
            name={`${id}-lead-paint`}
            value={LEAD_PAINT_INSPECT}
            checked={election === LEAD_PAINT_INSPECT}
            onChange={() => choose(LEAD_PAINT_INSPECT)}
            disabled={disabled}
            data-testid="lead-paint-option-inspect"
          />
          <span>{COPY.optionInspect}</span>
        </label>

        {election === LEAD_PAINT_INSPECT && (
          <div className={styles.days} data-testid="lead-paint-days-field">
            <label htmlFor={daysId} className={styles.daysLabel}>
              {COPY.daysLabel}
              <span className={styles.required} aria-hidden="true"> *</span>
            </label>
            <input
              id={daysId}
              className={styles.daysInput}
              type="number"
              inputMode="numeric"
              min={MIN_DEADLINE_DAYS}
              max={MAX_DEADLINE_DAYS}
              step="1"
              value={daysText}
              onChange={(e) => setDays(e.target.value)}
              disabled={disabled}
              required
              aria-describedby={[daysHelpId, daysError && daysErrorId].filter(Boolean).join(' ')}
              aria-invalid={Boolean(daysError || daysServerError) || undefined}
              data-testid="lead-paint-days"
            />
            <p id={daysHelpId} className={styles.hint}>{COPY.daysHelp}</p>
            {daysError && (
              <p id={daysErrorId} className={styles.error} role="alert">{daysError}</p>
            )}
            {daysServerError && (
              <p className={styles.error} role="alert" data-testid={`contingency-error-${DAYS_KEY}`}>
                {daysServerError}
              </p>
            )}
          </div>
        )}

        <label className={`${styles.option} ${election === LEAD_PAINT_WAIVE ? styles.optionOn : ''}`}>
          <input
            type="radio"
            name={`${id}-lead-paint`}
            value={LEAD_PAINT_WAIVE}
            checked={election === LEAD_PAINT_WAIVE}
            onChange={() => choose(LEAD_PAINT_WAIVE)}
            disabled={disabled}
            aria-describedby={waiveNoteId}
            data-testid="lead-paint-option-waive"
          />
          <span>{COPY.optionWaive}</span>
        </label>
        <p id={waiveNoteId} className={styles.waiveNote}>{COPY.waiveNote}</p>
      </div>

      {!election && (
        <p id={missingId} className={styles.missing} data-testid="lead-paint-missing">
          {COPY.missing}
        </p>
      )}
      {electionServerError && (
        <p className={styles.error} role="alert" data-testid={`contingency-error-${LEAD_PAINT_ELECTION_KEY}`}>
          {electionServerError}
        </p>
      )}
    </fieldset>
  );
}

/**
 * Shown to the seller instead of the question: legal copy file
 * contingency-notices-copy.v1.md §16, `WI.offer.lead_paint.seller_view.*`.
 */
export const LEAD_PAINT_CARRIED_COPY = {
  label: copyText('WI.offer.lead_paint.seller_view.label'),
  note: copyText('WI.offer.lead_paint.seller_view.note'),
  none: copyText('WI.offer.lead_paint.seller_view.none'),
  inspectNoDays: copyText('WI.offer.lead_paint.seller_view.inspect_no_days'),
};

/**
 * LeadPaintElectionCarried — the buyer's stored election, read-only, in a
 * seller's counter form. Renders nothing when the election isn't asked on
 * this listing (`mode` null).
 *
 * §16 rules: the label on its own line with the election's summary below it;
 * `.note` whenever the offer records an election; `.none` only when it records
 * none. An `inspect` election whose days can't be read is still an election,
 * so it shows `.inspect_no_days` and `.note`, never `.none`.
 *
 * Props:
 *   - mode: from `leadPaintElectionMode`
 *   - contingencies: the counter form's state (seeded from the offer)
 */
export function LeadPaintElectionCarried({ mode, contingencies }) {
  if (!mode) return null;
  const election = getLeadPaintElection(contingencies || {});
  const hasElection = election === LEAD_PAINT_INSPECT || election === LEAD_PAINT_WAIVE;
  const summary = hasElection
    ? getLeadPaintElectionSummary(contingencies || {}) || LEAD_PAINT_CARRIED_COPY.inspectNoDays
    : LEAD_PAINT_CARRIED_COPY.none;
  return (
    <div
      className={styles.carried}
      role="note"
      data-testid="lead-paint-election-carried"
      data-election={election || undefined}
    >
      <p className={styles.carriedLabel} data-testid="lead-paint-election-carried-label">
        <strong>{LEAD_PAINT_CARRIED_COPY.label}</strong>
      </p>
      <p className={styles.carriedLine} data-testid="lead-paint-election-carried-summary">{summary}</p>
      {hasElection && (
        <p className={styles.hint} data-testid="lead-paint-election-carried-note">
          {LEAD_PAINT_CARRIED_COPY.note}
        </p>
      )}
    </div>
  );
}
